import { useLocation } from 'react-router';
import type RouterService from '@ember/routing/router-service';
import { useRouter } from 'receivable-invoices-js/react/hooks/use-router';

interface EmberRouteMatch {
  handler: string;
  params: Record<string, string>;
}

type RecognizeResult = EmberRouteMatch[];

// Private fields we need on RouterService
interface EmberRouterInternals {
  _router: {
    _routerMicrolib: {
      recognizer: {
        recognize: (path: string) => RecognizeResult | undefined;
      };
    };
  };
}

function isRouterWithRecognizer(r: unknown): r is RouterService & EmberRouterInternals {
  try {
    return (
      typeof r === 'object' &&
      Boolean((r as EmberRouterInternals)._router._routerMicrolib.recognizer.recognize)
    );
  } catch {
    return false;
  }
}

/**
 * Determines whether the current route is handled by the Ember router.
 *
 * This hook inspects the application's router to check if the current path
 * matches any Ember route handler, excluding those that contain "react" in their name.
 *
 * @returns true if the current route is an Ember route (not a React route), otherwise false.
 *
 * @remarks
 * This is useful for hybrid applications where both Ember and React routes may coexist,
 * and you need to conditionally render or handle logic based on the routing system in use.
 */
export const useIsEmberRoute = (): boolean => {
  const location = useLocation();
  const router = useRouter();

  if (!isRouterWithRecognizer(router)) return false;

  // check if have ember route to handle the path
  const result = router._router._routerMicrolib.recognizer.recognize(location.pathname);
  if (!result?.length) return false;

  const routeName = result[result.length - 1]?.handler || '';
  return Boolean(routeName) && !/react/.exec(routeName);
};
